import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { User } from '@/types'

/**
 * The key the session lives under. Exported because it is no longer private to
 * this module: the exchange path reads the refresh token straight out of
 * storage, and the cross-tab listener has to recognise events about this key
 * and ignore every other one.
 */
export const AUTH_STORAGE_KEY = 'clearpoint-auth'

/**
 * The Web Storage area the session persists to — the one decision AR-2c is
 * about, made in the one place it can be made. The persist middleware, the
 * exchange's read, the idle clock, and the tests' sibling-tab fixtures all
 * resolve through here, so nothing can be left behind by a change to it.
 *
 * localStorage: one session for every tab in the browser, so Dana signs in
 * once instead of once per tab. The price is that it outlives the tab — on a
 * shared office machine, until someone logs out. The owner accepted that only
 * with a bound, and the bound ships with it: lib/idleExpiry.ts ends the
 * session after VITE_AUTH_IDLE_HOURS with no input, in every tab at once.
 * Moving the session anywhere else means re-reading that trade-off first.
 *
 * viewAsStore and dashboardStore stay per-tab, deliberately: an admin's
 * impersonation crossing into their other tabs would be a new defect.
 */
export function authStorageArea(): Storage {
  return localStorage
}

/**
 * Why a signed-out session is signed out, when another tab needs to know.
 * Only idleness is recorded: an explicit logout removes the key outright
 * (lib/auth.ts), and that absence is its own, different signal.
 */
export type SignOutReason = 'idle'

/** The slice `partialize` writes — the shape that actually reaches storage. */
export interface PersistedAuth {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  signOutReason: SignOutReason | null
}

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  /** Set only by expireForIdle(); cleared by any sign-in or plain sign-out. */
  signOutReason: SignOutReason | null
  isLoading: boolean

  setAuth: (user: User, accessToken: string, refreshToken: string) => void
  setTokens: (accessToken: string, refreshToken: string) => void
  clearAuth: () => void
  /**
   * End the session for idleness (lib/idleExpiry.ts). Signed out exactly as
   * clearAuth() leaves it, except the persisted envelope says WHY — so a
   * sibling tab can tell it from an explicit logout, which removes the key,
   * and sign out through the router instead of a document load, keeping
   * where she was.
   */
  expireForIdle: () => void
  setLoading: (loading: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      signOutReason: null,
      isLoading: true, // true until hydration completes

      setAuth: (user, accessToken, refreshToken) =>
        set({ user, accessToken, refreshToken, signOutReason: null, isLoading: false }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      clearAuth: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          signOutReason: null,
          isLoading: false,
        }),

      expireForIdle: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          signOutReason: 'idle',
          isLoading: false,
        }),

      setLoading: (isLoading) => set({ isLoading }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(authStorageArea),
      partialize: (state): PersistedAuth => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        signOutReason: state.signOutReason,
      }),
      onRehydrateStorage: () => {
        return (state) => {
          // Hydration complete — stop showing the loading screen
          state?.setLoading(false)
        }
      },
    },
  ),
)

/**
 * The auth slice inside a persisted `{state, version}` envelope, if one is
 * there. Nothing about the envelope is trusted: it is bytes another tab wrote,
 * possibly by an older build of this app, possibly truncated by a browser that
 * ran out of quota mid-write. Anything that is not the expected shape reads as
 * "no slice" rather than throwing into a caller that has no way to recover.
 */
export function persistedAuthFrom(envelope: unknown): Partial<PersistedAuth> | null {
  if (!envelope || typeof envelope !== 'object') return null
  const state = (envelope as { state?: unknown }).state
  if (!state || typeof state !== 'object') return null
  return state as Partial<PersistedAuth>
}

/** A storage value that is actually usable as a token. */
function asToken(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/**
 * The refresh token as it stands in STORAGE, not in this tab's memory.
 *
 * These diverge, and the divergence is the whole point. `/api/auth/refresh`
 * proxies GoTrue, which ROTATES: every exchange spends the token presented and
 * issues a new one. The rotation is written back through the persist
 * middleware, so it lands in storage and in the memory of the tab that ran it —
 * and nowhere near any other tab. A tab that reads its own in-memory snapshot
 * at exchange time is reading whatever it booted with, which a sibling tab may
 * have spent minutes ago. Storage is the one place the live token can be found.
 *
 * Reads through the persist middleware's own storage object rather than
 * touching `sessionStorage`/`localStorage` directly, so it follows the store
 * wherever it is configured to persist and cannot drift from it.
 */
export async function readPersistedRefreshToken(): Promise<string | null> {
  try {
    const storage = useAuthStore.persist.getOptions().storage
    const envelope = await storage?.getItem(AUTH_STORAGE_KEY)
    return asToken(persistedAuthFrom(envelope)?.refreshToken)
  } catch {
    // A browser with site data blocked throws on access. That is not a reason
    // to fail the exchange — the caller falls back to its in-memory copy.
    return null
  }
}
